import { useMemo, useState } from 'react';

import { C, FONT_MONO, LABEL_TRACKING, SUBLABEL_TRACKING } from '../../lib/design';
import type {
  ActualSetRow,
  CoachStrengthSession,
  ManualBodyweightActuals,
} from '../../lib/types';

type Props = {
  session: CoachStrengthSession;
  actuals: ActualSetRow[];
  saving: boolean;
  error: Error | null;
  onSave: (exercises: ManualBodyweightActuals['exercises']) => Promise<void>;
};

type DraftValue = number | '';
type Draft = Array<Array<{ repsDone?: DraftValue; durationS?: DraftValue }>>;

function isBodyweightSession(session: CoachStrengthSession): boolean {
  return session.exercises.every(
    (exercise) => exercise.group_id === undefined
      && exercise.sets.every((set) => set.weight_kg === 0),
  );
}

// Manual bodyweight actuals are keyed by the prescribed exercise's NAME (the
// identity key for a no-group exercise — see strength-session.ts), so a row
// matches its prescription slot by name + set index.
function makeDraft(session: CoachStrengthSession, actuals: ActualSetRow[]): Draft {
  const existing = new Map(
    actuals
      .filter((row) => row.source === 'manual')
      .map((row) => [`${row.exercise_key}|${row.set_idx}`, row]),
  );
  return session.exercises.map((exercise) =>
    exercise.sets.map((set, setIdx) => {
      const actual = existing.get(`${exercise.name}|${setIdx}`);
      return set.duration_s != null
        ? { durationS: actual?.duration_s ?? '' }
        : { repsDone: actual?.reps_done ?? '' };
    }),
  );
}

export function BodyweightActualEditor({ session, actuals, saving, error, onSave }: Props) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<Draft>(() => makeDraft(session, actuals));
  const hasActuals = actuals.some((row) => row.source === 'manual');
  const bodyweight = useMemo(() => isBodyweightSession(session), [session]);
  if (!bodyweight) return null;

  const openEditor = () => {
    setDraft(makeDraft(session, actuals));
    setOpen(true);
  };
  const updateValue = (
    exerciseIndex: number,
    setIdx: number,
    field: 'repsDone' | 'durationS',
    value: string,
  ) => {
    const parsed = Number(value);
    const next: DraftValue = value === '' || !Number.isFinite(parsed)
      ? ''
      : Math.max(field === 'durationS' ? 1 : 0, Math.trunc(parsed));
    setDraft((current) => current.map((sets, currentExercise) =>
      currentExercise !== exerciseIndex
        ? sets
        : sets.map((set, currentSet) => currentSet === setIdx
          ? { ...set, [field]: next }
          : set),
    ));
  };
  const draftComplete = session.exercises.every((exercise, exerciseIndex) =>
    exercise.sets.every((set, setIdx) => {
      const value = set.duration_s != null
        ? draft[exerciseIndex]?.[setIdx]?.durationS
        : draft[exerciseIndex]?.[setIdx]?.repsDone;
      return typeof value === 'number';
    }),
  );
  const save = async () => {
    if (!draftComplete) return;
    const exercises: ManualBodyweightActuals['exercises'] = draft.map((sets, exerciseIndex) => ({
      exercise_index: exerciseIndex,
      sets: sets.map((set, setIdx) => {
        if (set.durationS !== undefined) {
          return { set_idx: setIdx, duration_s: Number(set.durationS) };
        }
        return { set_idx: setIdx, reps_done: Number(set.repsDone) };
      }),
    }));
    try {
      await onSave(exercises);
      setOpen(false);
    } catch {
      // The mutation error is rendered below; keep the editor open so the
      // operator can correct/retry without losing the draft.
    }
  };

  return (
    <div style={{ marginTop: 12, border: `1px dashed ${C.line}`, padding: 10 }}>
      {!open ? (
        <button
          type="button"
          onClick={openEditor}
          style={{
            border: 0,
            background: 'transparent',
            color: hasActuals ? C.signal : C.warn,
            padding: 0,
            fontFamily: FONT_MONO,
            fontSize: 9,
            letterSpacing: LABEL_TRACKING,
            cursor: 'pointer',
          }}
        >
          {hasActuals ? 'EDIT BODYWEIGHT ACTUALS' : 'LOG BODYWEIGHT ACTUALS'}
        </button>
      ) : (
        <div>
          <div style={{ color: C.warn, fontSize: 9, lineHeight: 1.4, fontFamily: FONT_MONO }}>
            ENTER EVERY REP/TIME YOU ACTUALLY COMPLETED · PLAN VALUES ARE NOT COPIED
          </div>
          {session.exercises.map((exercise, exerciseIndex) => (
            <div key={`${exercise.name}-${exerciseIndex}`} style={{ marginTop: 10 }}>
              <div style={{ color: C.text, fontSize: 10, fontFamily: FONT_MONO }}>
                {exercise.name}
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 6 }}>
                {exercise.sets.map((set, setIdx) => {
                  const timed = set.duration_s != null;
                  const value = timed
                    ? draft[exerciseIndex]?.[setIdx]?.durationS ?? ''
                    : draft[exerciseIndex]?.[setIdx]?.repsDone ?? '';
                  return (
                    <label
                      key={setIdx}
                      style={{ color: C.dim, fontSize: 8, letterSpacing: SUBLABEL_TRACKING, fontFamily: FONT_MONO }}
                    >
                      SET {setIdx + 1} · {timed ? 'SEC' : 'REPS'}
                      <input
                        type="number"
                        min={timed ? 1 : 0}
                        step={1}
                        value={value}
                        onChange={(event) => updateValue(
                          exerciseIndex,
                          setIdx,
                          timed ? 'durationS' : 'repsDone',
                          event.target.value,
                        )}
                        style={{
                          display: 'block',
                          width: 64,
                          marginTop: 3,
                          border: `1px solid ${C.line}`,
                          background: C.bg,
                          color: C.text,
                          padding: '5px 6px',
                          fontFamily: FONT_MONO,
                          fontSize: 11,
                        }}
                      />
                    </label>
                  );
                })}
              </div>
            </div>
          ))}
          {error && (
            <div style={{ color: C.red, fontSize: 9, marginTop: 8, fontFamily: FONT_MONO }}>
              {error.message}
            </div>
          )}
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <button
              type="button"
              disabled={saving || !draftComplete}
              onClick={() => void save()}
              style={{
                border: `1px solid ${C.signal}`,
                background: C.signal,
                color: C.bg,
                padding: '6px 10px',
                fontFamily: FONT_MONO,
                fontSize: 9,
                letterSpacing: LABEL_TRACKING,
                cursor: saving ? 'wait' : draftComplete ? 'pointer' : 'not-allowed',
                opacity: draftComplete ? 1 : 0.5,
              }}
            >
              {saving ? 'SAVING…' : 'SAVE ACTUALS'}
            </button>
            <button
              type="button"
              disabled={saving}
              onClick={() => setOpen(false)}
              style={{
                border: `1px solid ${C.line}`,
                background: 'transparent',
                color: C.dim,
                padding: '6px 10px',
                fontFamily: FONT_MONO,
                fontSize: 9,
                letterSpacing: LABEL_TRACKING,
                cursor: 'pointer',
              }}
            >
              CANCEL
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
